import { LmsProviderEnum } from "@simbelapp/order-sdk";
import { ErrorCode } from "~/utils/enums/errors.enums";

/**
 * Seat arithmetic for « surréservation non autorisée », shared by the catalogue session modal, its
 * footer and `RequestTrainingPageContainer` so they cannot disagree.
 *
 * Orders applies the same rule on POST /bookings (`assertSessionHasSeats`): active bookings plus the
 * new attendees must not exceed `maxNbAttendees`, and company / group admins are exempt unless Docebo's
 * rules apply. Checking only "is the session already full" let a manager book 5 people on a 6/10
 * session in one go.
 *
 * The flag is read under both spellings: `/sessions/training` answers `overBookingNotAllowed`, while
 * `/sessionItems` takes `overbookingNotAllowed`. A `maxNbAttendees` of 0 or null means no cap.
 */
export interface SessionCapacity {
  nbAttendees?: number | null;
  maxNbAttendees?: number | null;
  overbookingNotAllowed?: boolean | null;
  overBookingNotAllowed?: boolean | null;
}

export interface SessionCapacityCheck {
  /** No seat left: the existing « session complète » path (request for the waiting list) applies. */
  isFullAndClosed: boolean;
  /** Seats remain, but fewer than the attendees selected: booking them all would overbook. */
  exceedsRemainingSeats: boolean;
  /** Seats left, or null when the session is uncapped or allows overbooking. */
  remainingSeats: number | null;
}

/** An empty attendee list books the current user alone, as `createBooking` does. */
export function selectedSeatCount(usersToInvite: unknown[] | null | undefined): number {
  return Math.max(1, usersToInvite?.length ?? 0);
}

/** A company synced with Docebo allows no overbooking at all: e-learning included, admins included. */
export function followsDoceboRules(lmsProvider: LmsProviderEnum | null | undefined): boolean {
  return lmsProvider === LmsProviderEnum.Docebo;
}

/**
 * « Bloquer les inscriptions une fois la capacité max atteinte » once the capacity changes: off without a
 * capacity, since an uncapped session is never full, and on with one under Docebo's rules.
 */
export function overbookingNotAllowedFor(
  overbookingNotAllowed: boolean | null | undefined,
  maxNbAttendees: number | null | undefined,
  followsDocebo: boolean,
): boolean {
  if (!Number.isFinite(maxNbAttendees)) return false;
  return followsDocebo || !!overbookingNotAllowed;
}

/** Whether `adding` more attendees takes the session past its capacity. */
export function exceedsCapacity(
  nbAttendees: number | null | undefined,
  adding: number,
  maxNbAttendees: number | null | undefined,
): boolean {
  return !!maxNbAttendees && Number(nbAttendees ?? 0) + adding > maxNbAttendees;
}

/**
 * Under Docebo's rules a session is closed whatever its own flag, and admins may not overbook it either:
 * Orders refuses them with SESSION_FULL (`assertSessionHasSeats`, `canOverbook`).
 */
export function checkSessionCapacity(
  session: SessionCapacity | null | undefined,
  selectedSeats: number,
  canOverbook: boolean,
  followsDocebo = false,
): SessionCapacityCheck {
  const overbookingNotAllowed = followsDocebo || !!(session?.overbookingNotAllowed || session?.overBookingNotAllowed);
  if (!session?.maxNbAttendees || !overbookingNotAllowed) {
    return { isFullAndClosed: false, exceedsRemainingSeats: false, remainingSeats: null };
  }

  const remainingSeats = Math.max(0, session.maxNbAttendees - Number(session.nbAttendees ?? 0));
  if (canOverbook && !followsDocebo) {
    return { isFullAndClosed: false, exceedsRemainingSeats: false, remainingSeats };
  }

  return {
    isFullAndClosed: remainingSeats === 0,
    exceedsRemainingSeats: remainingSeats > 0 && selectedSeats > remainingSeats,
    remainingSeats,
  };
}

export function remainingSeatsMessage(remainingSeats: number): string {
  return remainingSeats === 1
    ? "Il reste 1 place sur cette session : réduisez le nombre de participants"
    : `Il reste ${remainingSeats} places sur cette session : réduisez le nombre de participants`;
}

/** A capacity of at least 1 below the attendees means 2 of them or more: the copy is always plural. */
export function attendeesOverCapacityMessage(nbAttendees: number): string {
  return `${nbAttendees} participants sont déjà inscrits, au-delà de la capacité saisie.`;
}

export const CAPACITY_EXCEEDED_MESSAGE = "Vous dépassez la capacité maximale prévue pour cette session";

export const SESSION_FULL_MESSAGE = "Cette session n'a plus assez de places pour les participants sélectionnés";

/** Confirming a draft books all its attendees at once: there is no selection to blame. */
export const CONFIRM_PROJECT_SESSION_FULL_MESSAGE =
  "Impossible de confirmer : le nombre de participants dépasse la capacité maximale de la session. Retirez des participants ou augmentez la capacité.";

/**
 * Orders' 400 when the seats went to someone else between the page load and the submission, or when a
 * session synced with Docebo is full: Docebo refuses any overbooking, admins included.
 */
export function isSessionFullError(error: unknown): boolean {
  return (
    error === ErrorCode.SESSION_FULL || (error as { message?: unknown } | null)?.message === ErrorCode.SESSION_FULL
  );
}
